import { logAmplitudeEvent } from 'amplitude/amplitude'
import { orderSelectors } from 'ducks/orderComponents'
import { orgSelectors } from 'ducks/orgs'
import Button from 'elements/button/Button'
import type { ComponentLikeType, DistributorDataTypeV2 } from 'elements/hardwareSelectorV2/types'
import useTrackComponent from 'hooks/eventTracking/useTrackComponent'
import { useGlobalCart } from 'hooks/useGlobalCart'
import {
  Alert,
  Box,
  ComponentVersions_3_0,
  ComponentVersionsInherit,
  Icon,
  styled,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from 'opensolar-ui'
import MarketplaceDisclaimerBanner from 'pages/ordering/elements/MarketplaceDisclaimerBanner'
import AddItemDialog, { BomComponentSelectorType } from 'projectSections/sections/hardware/bom/AddItemDialog'
import ComponentSelectorDialog from 'projectSections/sections/hardware/bom/ComponentSelectorDialog'
import { useTranslate } from 'ra-core'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import type { RootState } from 'types/state'
import { ActionType, EventType } from 'types/tracking'
import { useFeatureFlag } from 'util/featureFlags'
import { formatDateString } from 'util/misc'
import { computeDealPrice, filterProductLevelDeals } from '../deals/dealEngine'
import type LineItemType from '../OrderLineItem'
import ProjectOrderPresenter from '../ProjectOrderPresenter/projectOrderPresenter'
import { CartPageSupplierAssistWidget } from './CartPageSupplierAssistWidget'
import CartTable from './CartTable'
import { ClearCartConfirmModal } from './ClearCartConfirmModal'
import { LeaveReviewCartPrompt } from './LeaveReviewCartPrompt'
import OrderSummaryPanel from './OrderSummaryPanel'
import type { ConsolidatedLineItem } from './useCartData'
import { toConsolidatedLineItem, useCartData } from './useCartData'
import { useLeaveReviewCartPrompt } from './useLeaveReviewCartPrompt'

const PageContainer = styled(Box)(({ theme }) => ({
  padding: 0,
  width: '100%',
  boxSizing: 'border-box',
  background: theme.palette.info?.light ?? '#F6F9FC',
  minHeight: '100vh',
}))

const ReadOnlyAlertWrapper = styled(Box)({
  marginTop: 16,
  marginLeft: 24,
  marginRight: 24,
  marginBottom: 16,
})

const ContentWrapper = styled(Box)({
  padding: '40px 60px',
  width: '100%',
  boxSizing: 'border-box',
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 9fr) minmax(0, 3fr)',
  gap: 24,
  '@media (max-width: 1200px)': {
    gridTemplateColumns: '1fr',
  },
})

const MainContent = styled(Box)({
  minWidth: 0,
  overflow: 'visible',
  alignSelf: 'flex-start',
})

const TableWrapper = styled(Box)({
  backgroundColor: 'white',
  borderRadius: 8,
  overflow: 'hidden',
  width: '100%',
  marginBottom: 24,
})

const BomHeaderSection = styled(Box)({
  backgroundColor: 'white',
  padding: '16px 24px',
  borderTopLeftRadius: 8,
  borderTopRightRadius: 8,
  borderBottom: '1px solid #ECECED',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
})

const MainTitle = styled(Typography)(({ theme }) => ({
  ...theme.typography.h5,
  color: theme.palette.neutralGrey[1300],
}))

const EmptyStateContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  paddingTop: '197px',
  minHeight: '70vh',
  backgroundColor: 'white',
  padding: '197px 40px 40px',
  width: '100%',
  boxSizing: 'border-box',
})

const EmptyStateHeading = styled(Typography)(({ theme }) => ({
  marginTop: 0,
  marginBottom: '32px',
  color: theme.palette.neutralGrey[900],
}))

const EmptyStateCards = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'center',
  gap: 16,
})

const EmptyStateCard = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.grey[200]}`,
  borderRadius: '8px',
  backgroundColor: theme.palette.neutralGrey[200],
  padding: '24px',
  minWidth: 280,
  maxWidth: 360,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 16,
}))

const EmptyStateCardTitle = styled(Typography)(({ theme }) => ({
  margin: 0,
  color: theme.palette.common.black,
  textAlign: 'center',
}))

const SortByRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
})

const ProjectSectionHeader = styled(Box)(({ theme }) => ({
  padding: '16px 24px 16px',
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  gap: 12,
  '& *': { fontSize: 14, color: theme.palette.grey[800] },
}))

const ProjectSectionHeaderRight = styled(Box)({
  display: 'flex',
  alignItems: 'baseline',
  gap: 16,
  flexShrink: 0,
})

const MainContentRight = styled(Box, { name: 'MainContentRight' })({
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  position: 'sticky',
  top: 24,
  alignSelf: 'start',
})

/** Cart Page: shows orderable (global) line items, Supplier Assist widget, and Go to Checkout. Rendered at /shop/cart. */
const ReviewCartPage = () => {
  const history = useHistory()
  const translate = useTranslate()

  const theme = useTheme()
  const {
    consolidatedLineItems,
    isLoading,
    hasLineItems,
    projectInfoMap,
    isSingleProject,
    systemIds,
    supplierAssistProjectData,
  } = useCartData()
  const { trackEvent } = useTrackComponent({
    componentKey: 'shop_cart_page',
    eventName: 'Hardware Cart',
  })

  const [showReadOnlyAlert, setShowReadOnlyAlert] = useState(true)
  const { totalItemCount, clearCart, orderableLineItems: confirmedCartLineItems } = useGlobalCart()
  const dispatch = useDispatch()
  const [sortBy, setSortBy] = useState<'category' | 'project'>('category')
  const currencySymbol = useSelector(orgSelectors.getCurrencySymbolFromOrg)
  const showReviewQuoteWidget = useFeatureFlag('review_quote_widget_in_cart', 'on')

  const unconfirmedLineItems = useSelector(orderSelectors.getUnconfirmedLineItems)
  // Value-based signature so projectGroups recomputes when confirmed items' distributors or quantities change.
  // The cached getConfirmedLineItems selector can return a stale reference when objects
  // are mutated in place, so we track a scalar that changes on every relevant update.
  const confirmedLineItemSignature = useSelector((state: RootState) =>
    state.orderComponents.lineItems
      .filter((item) => item.confirmed)
      .map((li) => `${li.uuid}:${li.selectedDistributor ?? ''}:${li.quantity}`)
      .join('|')
  )

  // Filter to only include line items that are orderable (available stock, quantity > 0, and have a selected supplier source)
  const orderableLineItems = useMemo(() => {
    return unconfirmedLineItems.filter((lineItem: LineItemType) => {
      const hasValidSource = lineItem.selectedDistributor && lineItem.selectedDistributor !== 'component'
      return (
        hasValidSource &&
        ProjectOrderPresenter.getStockLevelStatusFromLineItem(lineItem) !== 'not_available' &&
        lineItem.quantity > 0
      )
    })
  }, [unconfirmedLineItems])

  // Leave page confirmation prompt - clear line items when user confirms leaving to cancel destination
  const onConfirmLeave = useCallback(
    (path: string) => {
      const normalizedPath = (path || '/').replace(/\/$/, '') || '/'
      if (normalizedPath === '/multi_project_ordering') {
        clearCart()
      }
    },
    [dispatch]
  )

  const { markAsChanged, clearChanges, hasUnsavedChanges, isPromptOpen, continueNavigation, cancelNavigation } =
    useLeaveReviewCartPrompt({ onConfirmLeave })

  useEffect(() => {
    logAmplitudeEvent('page_viewed', {
      page: 'review_cart',
    })
  }, [])

  const handleGoToCheckout = useCallback(() => {
    logAmplitudeEvent('generic_button_clicked', {
      source: 'cart_page',
      context: 'go_to_checkout',
      totalLineItems: orderableLineItems.length,
    })
    history.push('/checkout')
  }, [history, orderableLineItems.length])

  // Check if all items have a valid source selected (supplier or manual cost option)
  const hasAllSourcesSelected = useMemo(() => {
    return consolidatedLineItems.every((lineItem) => {
      const source = lineItem.selectedDistributor
      return source && source !== 'component'
    })
  }, [consolidatedLineItems])

  const [clearCartModalOpen, setClearCartModalOpen] = useState(false)

  const { addLineItemFromV2Component } = useGlobalCart()

  // Project view: group confirmed cart line items by project (from useGlobalCart), filter loading, convert to ConsolidatedLineItem per slice
  const projectGroups = useMemo(() => {
    const raw = Array.isArray(confirmedCartLineItems) ? confirmedCartLineItems : []
    const items = raw.filter((li) => li && li.status !== 'loading')
    const projectIds = [
      ...new Set(
        items.flatMap((li) =>
          Array.isArray(li.projectOrder) ? li.projectOrder.map((po) => po?.projectId).filter(Boolean) : []
        )
      ),
    ].sort((a, b) => a - b) as number[]

    type Group = {
      projectId: number | null
      projectInfo?: { id: number; address: string; installation_date?: string }
      lineItems: ConsolidatedLineItem[]
      itemCount: number
      subtotal: number
    }

    const groupsMap = new Map<number | null, Group>()
    projectIds.forEach((id) => {
      groupsMap.set(id, {
        projectId: id,
        projectInfo: projectInfoMap[id],
        lineItems: [],
        itemCount: 0,
        subtotal: 0,
      })
    })
    if (!groupsMap.has(null)) {
      groupsMap.set(null, { projectId: null, lineItems: [], itemCount: 0, subtotal: 0 })
    }

    items.forEach((lineItem) => {
      if (lineItem.projectOrder && lineItem.projectOrder.length > 0) {
        lineItem.projectOrder.forEach((po) => {
          const group = groupsMap.get(po.projectId)
          if (!group) return
          const consolidated = toConsolidatedLineItem(lineItem, {
            hasMixedSources: false,
            isSingleProject: true,
            quantity: po.requiredQuantity,
            projectOrder: [po],
          })
          const sliceItem = { ...consolidated, uuid: `${lineItem.uuid}-${po.projectId}` }
          group.lineItems.push(sliceItem)
          const displayData = sliceItem.orderingData?.find(
            (d: DistributorDataTypeV2) => d.distributor === (sliceItem.resolvedSource ?? sliceItem.selectedDistributor)
          )
          const unitPrice = displayData?.price ?? (displayData as any)?.price_detail?.price_ex_vat ?? 0
          const deals = filterProductLevelDeals(displayData?.deals ?? [])
          const dealPrice = computeDealPrice(unitPrice, deals)
          group.itemCount += sliceItem.quantity
          group.subtotal += sliceItem.quantity * (dealPrice || unitPrice)
        })
      } else {
        const group = groupsMap.get(null)!
        const consolidated = toConsolidatedLineItem(lineItem, { hasMixedSources: false, isSingleProject: false })
        group.lineItems.push(consolidated)
        const displayData = consolidated.orderingData?.find(
          (d: DistributorDataTypeV2) =>
            d.distributor === (consolidated.resolvedSource ?? consolidated.selectedDistributor)
        )
        const unitPrice = displayData?.price ?? (displayData as any)?.price_detail?.price_ex_vat ?? 0
        const deals = filterProductLevelDeals(displayData?.deals ?? [])
        const dealPrice = computeDealPrice(unitPrice, deals)
        group.itemCount += consolidated.quantity
        group.subtotal += consolidated.quantity * (dealPrice || unitPrice)
      }
    })

    const result: Group[] = projectIds.map((id) => groupsMap.get(id)!).filter((g) => g.lineItems.length > 0)
    const noProject = groupsMap.get(null)!
    if (noProject.lineItems.length > 0) result.push(noProject)
    return result
  }, [confirmedCartLineItems, projectInfoMap, confirmedLineItemSignature])
  const [isAddItemActive, setIsAddItemActive] = useState(false)
  const [isOpenAddItemDialog, setIsOpenAddItemDialog] = useState(false)
  const [showSuggestion, setShowSuggestion] = useState(false)
  const [componentSelectorComponentType, setComponentSelectorComponentType] = useState<BomComponentSelectorType | null>(
    null
  )

  const handleAddItem = useCallback(() => {
    setIsAddItemActive(true)
  }, [])

  const handleSelectComponent = useCallback(
    (value: ComponentLikeType | { [key: string]: any }) => {
      const component = value as ComponentLikeType
      const orderingV2 = component?.ordering_v2
      const firstAvailable =
        Array.isArray(orderingV2) && (orderingV2.find((d: DistributorDataTypeV2) => d.is_available) ?? orderingV2[0])
      if (component && firstAvailable) {
        addLineItemFromV2Component(component, firstAvailable, 1)
      }
      setIsAddItemActive(false)
      setComponentSelectorComponentType(null)
    },
    [addLineItemFromV2Component]
  )

  const handleOpenAddItemDialog = useCallback(() => {
    setIsOpenAddItemDialog(true)
    setShowSuggestion(false)
  }, [])

  const handleCloseAddItemDialog = useCallback(() => {
    setIsOpenAddItemDialog(false)
  }, [])

  const handleSelectCategory = useCallback(
    (category: BomComponentSelectorType) => {
      handleCloseAddItemDialog()
      setComponentSelectorComponentType(category)
    },
    [handleCloseAddItemDialog]
  )

  const handleResetComponentSelectorComponentType = useCallback(() => {
    setComponentSelectorComponentType(null)
  }, [])

  const handleClearCart = useCallback(() => {
    setClearCartModalOpen(true)
  }, [])

  const handleClearCartConfirm = useCallback(() => {
    clearCart()
    setClearCartModalOpen(false)
  }, [clearCart])

  const handleClearCartCancel = useCallback(() => {
    setClearCartModalOpen(false)
  }, [])

  if (!hasLineItems && !isLoading) {
    return (
      <ComponentVersionsInherit versions={ComponentVersions_3_0}>
        <PageContainer>
          <ContentWrapper>
            <MainContent>
              <TableWrapper>
                <EmptyStateContainer>
                  <EmptyStateHeading textVariant="h3">{translate('Your Cart Is Empty')}</EmptyStateHeading>
                  <EmptyStateCards>
                    <EmptyStateCard>
                      <EmptyStateCardTitle textVariant="body1">
                        {translate('Explore deals and search our full range')}
                      </EmptyStateCardTitle>
                      <Button color="secondary" onClick={() => history.push('/shop')}>
                        {translate('Shop Home Page')}
                      </Button>
                    </EmptyStateCard>
                    <EmptyStateCard>
                      <EmptyStateCardTitle textVariant="body1">
                        {translate('Purchase BOMs from recent projects')}
                      </EmptyStateCardTitle>
                      <Button color="secondary" onClick={() => history.push('/multi_project_ordering')}>
                        {translate('Start A Multi-Project Order')}
                      </Button>
                    </EmptyStateCard>
                  </EmptyStateCards>
                </EmptyStateContainer>
              </TableWrapper>
            </MainContent>
            <Box display="flex" flexDirection="column" style={{ gap: 16 }}>
              {showReviewQuoteWidget && (
                <CartPageSupplierAssistWidget
                  systemIds={systemIds}
                  orderableLineItems={consolidatedLineItems}
                  projectData={supplierAssistProjectData}
                />
              )}
              <OrderSummaryPanel lineItems={[]} onProceedToCheckout={handleGoToCheckout} disabled />
              <MarketplaceDisclaimerBanner showIcon={false} />
            </Box>
          </ContentWrapper>
        </PageContainer>
      </ComponentVersionsInherit>
    )
  }

  return (
    <ComponentVersionsInherit versions={ComponentVersions_3_0}>
      <PageContainer>
        <ContentWrapper>
          <MainContent>
            <TableWrapper>
              <BomHeaderSection>
                <MainTitle>{translate('Your Cart')}</MainTitle>

                <SortByRow>
                  <Typography variant="body2Medium" sx={{ color: 'common.black' }}>
                    {translate('Group by')}
                  </Typography>
                  <ToggleButtonGroup
                    value={sortBy}
                    exclusive
                    onChange={(_e, value: 'category' | 'project' | null) => {
                      if (!value) return
                      setSortBy(value)
                      trackEvent(
                        EventType.USER_INTERACTION,
                        { type: ActionType.CLICK },
                        { source: `group_by_${value}` }
                      )
                    }}
                  >
                    <ToggleButton value="category" color={sortBy === 'category' ? 'info' : 'secondary'}>
                      <Icon icon={'widgets'} size={'small'} style={{ marginRight: 6 }} />
                      {translate('Category')}
                    </ToggleButton>
                    <ToggleButton value="project" color={sortBy === 'project' ? 'info' : 'secondary'}>
                      <Icon icon={'location_on'} size={'small'} style={{ marginRight: 6 }} />
                      {translate('Project')}
                    </ToggleButton>
                  </ToggleButtonGroup>
                </SortByRow>
              </BomHeaderSection>
              {sortBy === 'project' && showReadOnlyAlert && (
                <ReadOnlyAlertWrapper>
                  <Alert severity="info" onPressCloseIcon={() => setShowReadOnlyAlert(false)}>
                    {translate('Editing is disabled in Project View. To make changes, switch to Group by Category')}
                  </Alert>
                </ReadOnlyAlertWrapper>
              )}
              <Box>
                {sortBy === 'category' ? (
                  <>
                    <div style={{ padding: '16px 24px 28px', overflowX: 'auto' }}>
                      <CartTable
                        lineItems={consolidatedLineItems}
                        isLoading={isLoading}
                        projectInfoMap={projectInfoMap}
                        isSingleProject={isSingleProject}
                        onChangesMade={markAsChanged}
                        isProjectView={false}
                        onClearCartClick={handleClearCart}
                        addItemRow={
                          isAddItemActive
                            ? {
                                showSuggestion,
                                setShowSuggestion,
                                onSelectComponent: (item: unknown) => handleSelectComponent(item as ComponentLikeType),
                                onBrowse: handleOpenAddItemDialog,
                              }
                            : undefined
                        }
                      />
                    </div>
                  </>
                ) : null}
                {sortBy === 'project' && projectGroups.length === 0 && (
                  <Box padding={3} textAlign="center">
                    <Typography variant="body2" color="textSecondary">
                      {translate('No items in cart')}
                    </Typography>
                  </Box>
                )}
                {sortBy === 'project' &&
                  projectGroups.length > 0 &&
                  projectGroups.map((group, index) => (
                    <Box key={group.projectId ?? 'no-project'}>
                      <ProjectSectionHeader>
                        <Typography variant="body2" sx={{ color: 'common.black' }}>
                          {group.projectId != null
                            ? `${translate('Project')}: ${group.projectInfo?.address ?? ''}`
                            : translate('Items not assigned to a project')}
                        </Typography>
                        <ProjectSectionHeaderRight>
                          <Typography variant="body2" sx={{ color: 'common.black' }}>
                            {translate('Install Date')}:{' '}
                            {group.projectInfo?.installation_date
                              ? formatDateString(group.projectInfo.installation_date)
                              : '-'}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'common.black' }}>
                            {translate('Subtotal')} ({group.itemCount}{' '}
                            {group.itemCount === 1 ? translate('item') : translate('items')}): {currencySymbol ?? ''}
                            {group.subtotal.toFixed(2)}
                          </Typography>
                        </ProjectSectionHeaderRight>
                      </ProjectSectionHeader>
                      <div style={{ padding: '0 24px 24px', overflowX: 'auto' }}>
                        <CartTable
                          lineItems={group.lineItems}
                          isLoading={isLoading}
                          projectInfoMap={projectInfoMap}
                          isSingleProject={true}
                          isProjectView={true}
                        />
                      </div>
                    </Box>
                  ))}
              </Box>
            </TableWrapper>
          </MainContent>

          <MainContentRight>
            {showReviewQuoteWidget && (
              <CartPageSupplierAssistWidget
                systemIds={systemIds}
                orderableLineItems={consolidatedLineItems}
                projectData={supplierAssistProjectData}
              />
            )}
            <OrderSummaryPanel
              lineItems={consolidatedLineItems}
              onProceedToCheckout={handleGoToCheckout}
              disabled={!hasAllSourcesSelected || consolidatedLineItems.length === 0}
            />
            <MarketplaceDisclaimerBanner showIcon={false} />
          </MainContentRight>
        </ContentWrapper>

        {isOpenAddItemDialog && (
          <AddItemDialog
            handleClose={handleCloseAddItemDialog}
            handleSelectCategory={handleSelectCategory}
            title={translate('Add Item')}
            content={translate('Select a component category to add to your order.')}
          />
        )}
        {componentSelectorComponentType && (
          <ComponentSelectorDialog
            handleClose={handleResetComponentSelectorComponentType}
            componentType={componentSelectorComponentType}
            handleSelect={handleSelectComponent}
            skipProjectPermissionCheck={true}
          />
        )}
        <LeaveReviewCartPrompt open={isPromptOpen} onCancel={cancelNavigation} onConfirm={continueNavigation} />
        <ClearCartConfirmModal
          open={clearCartModalOpen}
          itemCount={totalItemCount}
          onCancel={handleClearCartCancel}
          onConfirm={handleClearCartConfirm}
        />
      </PageContainer>
    </ComponentVersionsInherit>
  )
}

export default ReviewCartPage
